import { useLayoutEffect, useRef } from "react";
import type { RowData } from "@tanstack/react-table";
import { isEqual } from "@common/helpers/comparison";
import type { DataGridReactTable } from "../../types";

/**
 * Client pagination: a new sort, search, or column filter goes back to the
 * first page, and a page that no longer exists (rows filtered or removed)
 * moves to the last one. With no rows yet (still loading) the page is kept,
 * so a restored page survives until rows arrive. TanStack's own auto reset
 * stays off because it also fires on every `data` change, which would jump
 * an edit back to page 1.
 * Values are compared, not identities, so a controlled slice rebuilt each
 * render doesn't pin the grid to page 1. Server pagination is untouched.
 */
export const useClientPageReset = <TData extends RowData>(
    table: DataGridReactTable<TData>,
) => {
    const { sorting, globalFilter, columnFilters, pagination } = table.state;
    const isClientPaged = !table.options.manualPagination;
    const pageCount = isClientPaged ? table.getPageCount() : 0;
    const rowQueryRef = useRef({ sorting, globalFilter, columnFilters });

    useLayoutEffect(() => {
        if (!isClientPaged) {
            return;
        }
        const rowQuery = { sorting, globalFilter, columnFilters };
        const hasNewRowQuery = !isEqual(rowQuery, rowQueryRef.current);
        rowQueryRef.current = rowQuery;
        if (hasNewRowQuery && pagination.pageIndex !== 0) {
            table.setPageIndex(0);
        } else if (pageCount > 0 && pagination.pageIndex >= pageCount) {
            table.setPageIndex(pageCount - 1);
        }
    }, [
        table,
        isClientPaged,
        sorting,
        globalFilter,
        columnFilters,
        pagination.pageIndex,
        pageCount,
    ]);
};
